
import Vue, { PropType } from "vue";
import KFieldGroup from "@/components/crud/fields/KFieldGroup.vue";
import KTextField from "@/components/crud/fields/KTextField.vue";
import dayjs from "@/plugins/dayjs";
import SourceAutocomplete from "@/modules/salesOrder/components/SourceAutocomplete.vue";
import SalesOrderStatusAutocomplete from "@/modules/salesOrder/components/SalesOrderStatusAutocomplete.vue";
import { mapGetters } from "vuex";
import ProductAutocomplete from "@/modules/delivery/components/ProductAutocomplete.vue";
import { SalesOrderStatus } from "@/modules/salesOrder/enums";
import deepDiff from "@/application/util/deepDiff";

type ComponentData = {
  internalFilters: Record<string, any>;
  defaultFilters: Record<string, unknown>;
};

export default Vue.extend({
  name: "SalesOrderFilter",
  components: {
    ProductAutocomplete,
    SalesOrderStatusAutocomplete,
    SourceAutocomplete,
    KTextField,
    KFieldGroup,
  },
  props: {
    filters: {
      type: Object as PropType<Record<string, any>>,
      required: true,
    },
  },
  data: (): ComponentData => ({
    internalFilters: {
      orderDateFrom: undefined,
      orderDateTo: undefined,
      source: undefined,
      salesOrderStatusId: undefined,
      articleNumber: undefined,
    },
    defaultFilters: {
      orderDateFrom: dayjs()
        .subtract(3, "month")
        .startOf("date")
        .format("YYYY-MM-DD HH:mm:ss"),
      orderDateTo: dayjs().endOf("date").format("YYYY-MM-DD HH:mm:ss"),
      salesOrderStatusId: [
        SalesOrderStatus.New,
        SalesOrderStatus.Alert,
        SalesOrderStatus.PickAndPack,
        SalesOrderStatus.PendingCustomerLabels,
        SalesOrderStatus.NotCollected,
      ],
    },
  }),
  methods: {
    resetFilterValues() {
      const difference = deepDiff(
        this.internalFilters || {},
        this.defaultFilters || {}
      );
      if (!Object.keys(difference)?.length) return;
      this.internalFilters = JSON.parse(JSON.stringify(this.defaultFilters));
    },
  },
  computed: {
    ...mapGetters("authorisation", ["client"]),
    orderDateFrom: {
      get: function () {
        if (!this.internalFilters?.orderDateFrom) return;
        return dayjs(this.internalFilters?.orderDateFrom).format("YYYY-MM-DD");
      },
      set: function (newValue?: string) {
        this.internalFilters.orderDateFrom = newValue
          ? dayjs(newValue).startOf("date").format("YYYY-MM-DD HH:mm:ss")
          : newValue;
      },
    },
    orderDateTo: {
      get: function () {
        if (!this.internalFilters?.orderDateTo) return;
        return dayjs(this.internalFilters?.orderDateTo).format("YYYY-MM-DD");
      },
      set: function (newValue?: string) {
        this.internalFilters.orderDateTo = newValue
          ? dayjs(newValue).endOf("date").format("YYYY-MM-DD HH:mm:ss")
          : newValue;
      },
    },
  },
  watch: {
    filters: {
      handler(newFilters) {
        this.internalFilters = newFilters;
      },
      immediate: true,
      deep: true,
    },
    internalFilters: {
      handler(newFilters) {
        this.$emit("update:filters", newFilters);
        if (!newFilters) return;

        sessionStorage.setItem("salesOrderFilters", JSON.stringify(newFilters));
      },
      deep: true,
    },
  },
});
